import debounce from 'lodash-es/debounce'
import { PropType, computed, defineComponent, onMounted, ref } from 'vue'

import { MsChevron } from '@sunrise/icons'
import { screenSizeDetection } from '@sunrise/shared'

import {
    calculateIntersection,
    isScrolledToEnd,
    isScrolledToStart,
    readjust,
    scrollToNext,
    scrollToPrevious,
} from './MsItemSliderUtils'

export default defineComponent({
    components: {
        MsChevron,
    },
    props: {
        listTag: {
            type: String as PropType<keyof HTMLElementTagNameMap>,
            default: 'ul',
        },
    },
    emits: ['scroll', 'afterScroll', 'prevClick', 'nextClick'],
    setup(props, { emit, expose }) {
        const { isScreenM } = screenSizeDetection()
        const scrollableElement = ref<HTMLDivElement | null>(null)
        const isPrevButtonVisible = ref(false)
        const isNextButtonVisible = ref(!!isScreenM.value)

        const listElements = ['ul', 'ol', 'li']
        const listRole = computed(() => (!listElements.includes(props.listTag) ? 'list' : undefined))

        const attachEvents = () => {
            const afterScrollHandler = debounce(async () => {
                scrollableElement.value?.removeEventListener('scroll', afterScrollHandler)
                await readjust(scrollableElement.value as HTMLDivElement)
                scrollableElement.value?.addEventListener('scroll', afterScrollHandler)
                emit('afterScroll')
            }, 300)

            const visibleButtonsUpdateHandler = () => {
                updateStartAndEnd()
            }

            scrollableElement.value?.addEventListener('scroll', () => emit('scroll'))
            scrollableElement.value?.addEventListener('scroll', afterScrollHandler)
            scrollableElement.value?.addEventListener('scroll', visibleButtonsUpdateHandler)
        }

        const updateStartAndEnd = () => {
            if (!scrollableElement.value) {
                return
            }

            if (!isScreenM.value) {
                isPrevButtonVisible.value = false
                isNextButtonVisible.value = false
                return
            }

            isPrevButtonVisible.value = !isScrolledToStart(scrollableElement.value)
            isNextButtonVisible.value = !isScrolledToEnd(scrollableElement.value)
        }

        expose({
            updateStartAndEnd,
            scrollableElement,
            calculateIntersection: () =>
                scrollableElement.value ? calculateIntersection(scrollableElement.value) : null,
        })

        const onNextClick = (event: MouseEvent) => {
            scrollToNext(scrollableElement.value as HTMLDivElement)
            emit('nextClick', event)
        }

        const onPrevClick = (event: MouseEvent) => {
            scrollToPrevious(scrollableElement.value as HTMLDivElement)
            emit('prevClick', event)
        }

        onMounted(() => {
            attachEvents()
            updateStartAndEnd()
        })

        return {
            scrollableElement,
            isPrevButtonVisible,
            isNextButtonVisible,
            listRole,
            onNextClick,
            onPrevClick,
        }
    },
})
